import { AdUnit } from '@pubstack/common/src/adunit'
import { MappableAdUnit } from '~/modules/pbs/integrations/bidder-seats/bidder-seat/mappingInventory'
import { BidderSeat, productOfBidderSeatType } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { Site } from '@pubstack/common/src/site'
import { FunctionComponent, ReactNode, useMemo } from 'react'
import { NoDataSpreadsheet } from '~/components/_styled/spreadsheet'
import { CollapsiblePanel } from '~/components/CollapsiblePanel'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { WidgetMessage } from '~/components/WidgetMessage'
import { BidderMapping } from '~/modules/pbs/integrations/bidder-seats/bidder-seat/BidderMapping'
import { WithClassName } from '~/types/utils'
import { MARKETPLACE_MAIL } from '~/utils/constants'

const MARKETPLACE_MAIL_TRACKING_LABEL: TrackingLabel = `adm-mapping-send-marketplace-mail-apply`

type Props = WithClassName & {
  readOnly: boolean
  adUnits: AdUnit[]
  bidderSeat?: BidderSeat
  catalogBidder?: BidderCatalog
  title: ReactNode
  onSaveSuccess: () => void
  currentSite?: Site
}

export const AdmIntegrationMapping: FunctionComponent<Props> = ({ className, readOnly, adUnits, bidderSeat, catalogBidder, title, onSaveSuccess, currentSite }) => {
  // One's inventory only, so it says so rather than being recognised by carrying no `env`.
  const admMappableAdUnits = useMemo<MappableAdUnit[]>(() => adUnits.map((adUnit) => ({ ...adUnit, product: 'adm' as const })), [adUnits])

  return (
    <CollapsiblePanel isCollapsible={{ trackingLabel: 'adm-mapping-bidder-collapse-apply' }} hasBorderWhenOpened className={className} title={title}>
      <div className="flex flex-col gap-[1.125rem]">
        {readOnly && (
          <WidgetMessage
            message={
              <div>
                This is a Marketplace integration. Mapping and bid adjustment are managed by Pubstack. Please contact{' '}
                <Link label={MARKETPLACE_MAIL} href={`mailto:${MARKETPLACE_MAIL}`} trackingLabel={MARKETPLACE_MAIL_TRACKING_LABEL} variant="secondary" /> if you spot an issue or have questions.
              </div>
            }
            icon={'tips'}
          />
        )}
        {!bidderSeat || !catalogBidder ? (
          <NoDataSpreadsheet>
            <Icon name="night_sky" width="144px" />
            <div>Something went wrong while loading data</div>
          </NoDataSpreadsheet>
        ) : (
          <BidderMapping
            adUnits={admMappableAdUnits}
            bidderSeat={bidderSeat}
            catalogBidder={catalogBidder}
            onSaveSuccess={onSaveSuccess}
            platform={bidderSeat.platform}
            // A Node seat is mapped here too, on One's ad units, and its rows go through Node's API.
            product={productOfBidderSeatType(bidderSeat.type)}
            disabled={readOnly}
            currentSite={currentSite}
          />
        )}
      </div>
    </CollapsiblePanel>
  )
}
